import { FC, ReactNode, useState } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbEdit, TbEye, TbEyeOff, TbLink, TbTrash } from 'react-icons/tb';

import { useNestedResourceAccess } from '@/hooks/useNestedResourceAccess';
import RenameResourceModal from '@/shared/components/modals/ResourceActionModals/01-RenameResourceModal';
import MoveResourceToFolderModal from '@/shared/components/modals/ResourceActionModals/02-MoveResourceToFolderModal';
import DeleteResourceModal from '@/shared/components/modals/ResourceActionModals/03-DeleteResourceModal';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    getWorkspaceResourceStatusToggleLabel,
    WorkspaceResourceStatusActionIcon,
} from '@/shared/components/workspace-resources';
import { useDashboardStore, useReportStore } from '@/stores';
import { Report } from '@/types';

import { WorkspaceResourceActionMenuItem } from '../helpers';
import WorkspaceResourceActionsDropdown from '../WorkspaceResourceActionsDropdown';

interface ReportsActionsDropdownProps {
    report: Report;
    children: ReactNode;
    onOpenChange?: (open: boolean) => void;
}

const ReportsActionsDropdown: FC<ReportsActionsDropdownProps> = ({ children, onOpenChange, report }): JSX.Element => {
    const [renameVisible, setRenameVisible] = useState(false);
    const [moveToFolderVisible, setMoveToFolderVisible] = useState(false);
    const [deleteVisible, setDeleteVisible] = useState(false);

    const { selectedDashboard } = useDashboardStore();
    const { duplicateReport, toggleReportLock, toggleReportArchive, updateReport } = useReportStore();
    const access = useNestedResourceAccess('reports', report?.id);

    const handleToggleLock = async () => {
        if (!access.canManage) {
            return;
        }

        toggleReportLock(report?.id, !report?.locked);
    };

    const handleToggleArchive = async () => {
        if (!access.canManage || access.locked) {
            return;
        }

        toggleReportArchive(report?.id, !report?.archived);
    };

    const handleToggleHidden = async () => {
        if (!access.canUpdate) {
            return;
        }

        updateReport(report?.id, { closed: !report?.closed });
    };

    const handleDuplicateReport = async () => {
        if (!access.canRead || !access.canCreateSibling) {
            return;
        }

        duplicateReport(report?.id);
    };

    const items: WorkspaceResourceActionMenuItem[] = [
        // {
        //     key: 'favorited',
        //     icon: report?.favorited ? <TbStarOff size={'1rem'} /> : <TbStar size={'1rem'} />,
        //     label: report?.favorited ? 'Remove from Favorites' : 'Add to Favorites',
        //     onClick: handleToggleFavorite,
        // },
        {
            key: 'locked',
            icon: <WorkspaceResourceStatusActionIcon active={report?.locked} status='locked' />,
            label: getWorkspaceResourceStatusToggleLabel('locked', report?.locked),
            onClick: handleToggleLock,
            permission: 'manage',
        },
        {
            key: 'archived',
            icon: <WorkspaceResourceStatusActionIcon active={report?.archived} status='archived' />,
            label: getWorkspaceResourceStatusToggleLabel('archived', report?.archived),
            onClick: handleToggleArchive,
            permission: 'manage',
            disabled: access.locked,
        },
        {
            key: 'hidden',
            icon: report?.closed ? <TbEyeOff size={'1rem'} /> : <TbEye size={'1rem'} />,
            label: report?.closed ? 'Show' : 'Hide',
            onClick: handleToggleHidden,
            permission: 'update',
        },
        {
            key: 'copyLink',
            icon: <TbLink size={'1rem'} />,
            label: 'Copy Link',
            onClick: () => {
                navigator.clipboard
                    .writeText(
                        `${
                            import.meta.env.DEV ? 'http://localhost:3000' : 'https://dashboard.countercyclical.io'
                        }/dashboards/${selectedDashboard?.id}?report=${report?.id}`,
                    )
                    .then(
                        () => {
                            toast('Link Copied', {
                                icon: <TbLink size='1.125rem' style={{ marginRight: '0.5rem' }} />,
                                description: report?.name,
                            });
                        },
                        (error) => {
                            toast('Error Copying Link', {
                                description: report?.name,
                            });

                            captureException(error);
                        },
                    );
            },
            permission: 'read',
        },
        // {
        //     key: 'openInNewWindow',
        //     icon: <TbExternalLink size={'1rem'} />,
        //     label: 'Open in New Window',
        //     onClick: () => {
        //         window.open(`/investments/${report?.id}`, '_blank');
        //     },
        // },
        {
            key: 'rename',
            icon: <TbEdit size={'1rem'} />,
            label: 'Rename',
            onClick: () => {
                setRenameVisible(true);
            },
            permission: 'update',
        },
        {
            key: 'duplicate',
            icon: <BiDuplicate size={'1rem'} />,
            label: 'Duplicate',
            onClick: handleDuplicateReport,
            permission: 'read',
            disabled: !access.canCreateSibling,
        },
        // {
        //     key: 'templatized',
        //     icon: templatized ? <TbIcons size={'1rem'} /> : <TbIconsOff size={'1rem'} />,
        //     label: templatized ? 'Revert Template' : 'Convert to Template',
        //     onClick: () => {
        //         authorizedAxiosInstance
        //             .put(`/api/investments/${listView?.id}`, {
        //                 isTemplate: !templatized,
        //             })
        //             .then((res: AxiosResponse) => {
        //                 if (res.status === 200) {
        //                     setArchived(res?.data?.isTemplate);

        //                     toast(`Investment ${archived ? 'Template Reverted' : 'Converted to Template'}`, {
        //                         icon: res?.data?.isTemplate ? <TbIconsOff size={'1rem'} /> : <TbIcons size={'1rem'} />,
        //                         description: res?.data?.name,
        //                     });
        //                 }
        //             });
        //     },
        //     disabled: true,
        // },
        // {
        //     key: 'transfer',
        //     icon: <TbArrowsHorizontal size="1rem" />,
        //     label: 'Transfer',
        //     onClick: () => {
        //         return;
        //     },
        //     disabled: true,
        // },
        // {
        //     key: 'moveToFolder',
        //     icon: <TbFolderSymlink size='1rem' />,
        //     label: 'Move to Folder',
        //     onClick: () => {
        //         setMoveToFolderVisible(true);
        //     },
        // },
        {
            key: 'delete',
            icon: <TbTrash size={'1rem'} />,
            label: 'Delete',
            onClick: () => {
                setDeleteVisible(true);
            },
            danger: true,
            permission: 'manage',
            disabled: access.locked,
        },
    ];

    return (
        <>
            <WorkspaceResourceActionsDropdown
                access={access}
                history={{ resourceId: report.id, resourceType: 'reports', title: report.name }}
                items={items}
                menuStyle={{
                    marginTop: '0.5rem',
                    minWidth: 16 * 18,
                }}
                placement='bottomLeft'
                resourceLabel='Report'
                stopPropagation
                onOpenChange={onOpenChange}
            >
                {children}
            </WorkspaceResourceActionsDropdown>
            {/* Action Modals */}
            <RenameResourceModal
                open={renameVisible}
                setOpen={setRenameVisible}
                resource={report}
                singularResource='report'
            />
            <MoveResourceToFolderModal
                open={moveToFolderVisible}
                setOpen={setMoveToFolderVisible}
                resource={report}
                singularResource='report'
            />
            <DeleteResourceModal
                open={deleteVisible}
                setOpen={setDeleteVisible}
                resource={report}
                singularResource='report'
            />
        </>
    );
};

export default ReportsActionsDropdown;
